import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Text,
  Card,
  CardBody,
  useToast,
  Icon,
  Collapse,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FaEnvelope, FaPhone, FaUser, FaCheckCircle } from "react-icons/fa";
import { gql, useMutation } from "@apollo/client";
import { CalculatorType, SubmitCalculatorInput } from "../../generated/graphql";

const SUBMIT_CALCULATOR = gql`
  mutation SubmitCalculator($input: SubmitCalculatorInput!) {
    submitCalculator(input: $input) {
      id
      calculatorType
      isLeadCaptured
      createdAt
    }
  }
`;

interface LeadCaptureFormProps {
  calculatorType: CalculatorType;
  calculatorName: string;
  calculatorPath: string;
  inputData: Record<string, any>;
  resultData: Record<string, any>;
  hasResults: boolean;
  accentColor?: string;
}

export const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({
  calculatorType,
  calculatorName,
  calculatorPath,
  inputData,
  resultData,
  hasResults,
  accentColor = "blue",
}) => {
  const toast = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    marketingConsent: false,
  });

  const [submitCalculator, { loading }] = useMutation(SUBMIT_CALCULATOR);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getAnalyticsData = () => {
    // Get visitor/session IDs from localStorage/sessionStorage if available
    const visitorId = localStorage.getItem("bb_visitor_id") || undefined;
    const sessionId = sessionStorage.getItem("bb_session_id") || undefined;

    // Get UTM params from URL
    const urlParams = new URLSearchParams(window.location.search);

    return {
      visitorId,
      sessionId,
      referrer: document.referrer || undefined,
      utmSource: urlParams.get("utm_source") || undefined,
      utmMedium: urlParams.get("utm_medium") || undefined,
      utmCampaign: urlParams.get("utm_campaign") || undefined,
      userAgent: navigator.userAgent,
    };
  };

  const handleSubmit = async (captureLeadInfo: boolean) => {
    try {
      const analyticsData = getAnalyticsData();

      const input: SubmitCalculatorInput = {
        calculatorType,
        calculatorName,
        calculatorPath,
        inputData: JSON.stringify(inputData),
        resultData: JSON.stringify(resultData),
        ...(captureLeadInfo && {
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          marketingConsent: formData.marketingConsent,
        }),
        ...analyticsData,
      };

      await submitCalculator({ variables: { input } });

      setIsSubmitted(true);

      if (captureLeadInfo && formData.email) {
        toast({
          title: "Thank you!",
          description: "We'll be in touch soon with personalized advice.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error submitting calculator:", error);
      toast({
        title: "Error",
        description: "Failed to save your results. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Only show when there are results
  if (!hasResults) return null;

  // Show success message after submission
  if (isSubmitted) {
    return (
      <Card bg={`${accentColor}.50`} borderWidth={2} borderColor={`${accentColor}.300`}>
        <CardBody>
          <VStack spacing={3}>
            <Icon as={FaCheckCircle} boxSize={10} color={`${accentColor}.500`} />
            <Text fontWeight="bold" color={`${accentColor}.700`} fontSize="lg">
              Results Saved!
            </Text>
            {formData.email && (
              <Text fontSize="sm" color="gray.600" textAlign="center">
                A specialist will review your calculation and contact you soon.
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card borderWidth={1} borderColor="gray.200">
      <CardBody>
        <VStack spacing={4} align="stretch">
          <Text fontWeight="bold" color="gray.700">
            Get Personalized Advice
          </Text>

          <Text fontSize="sm" color="gray.600">
            Want a specialist to review your results and provide tailored recommendations?
          </Text>

          {!showForm ? (
            <VStack spacing={3}>
              <Button
                colorScheme={accentColor}
                size="lg"
                width="full"
                onClick={() => setShowForm(true)}
                leftIcon={<Icon as={FaEnvelope} />}
              >
                Yes, Contact Me
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSubmit(false)}
                isLoading={loading}
              >
                No thanks, just save my results
              </Button>
            </VStack>
          ) : (
            <Collapse in={showForm}>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">First Name</FormLabel>
                    <Input
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange("firstName", e.target.value)}
                      size="md"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Last Name</FormLabel>
                    <Input
                      placeholder="Smith"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange("lastName", e.target.value)}
                      size="md"
                    />
                  </FormControl>
                </HStack>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    size="md"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Phone (optional)</FormLabel>
                  <Input
                    type="tel"
                    placeholder="0400 000 000"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    size="md"
                  />
                </FormControl>

                <Checkbox
                  isChecked={formData.marketingConsent}
                  onChange={(e) => handleInputChange("marketingConsent", e.target.checked)}
                  size="sm"
                  colorScheme={accentColor}
                >
                  <Text fontSize="xs" color="gray.600">
                    I agree to receive helpful tips and updates via email
                  </Text>
                </Checkbox>

                <Button
                  colorScheme={accentColor}
                  size="lg"
                  onClick={() => handleSubmit(true)}
                  isLoading={loading}
                  isDisabled={!formData.email}
                >
                  Get Expert Advice
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
              </VStack>
            </Collapse>
          )}

          <Text fontSize="xs" color="gray.500" textAlign="center">
            Your information is secure and will never be shared with third parties.
          </Text>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default LeadCaptureForm;
